import { Rower, RowerAnnotationOptions } from './Rower';
import { Coxswain } from './Coxswain';
import { CREW_CONFIG } from './constants';
import { calculateCrewPositions, isPortSide } from './utils';
import { PlaybackState } from './PlaybackControls';
import { useAnimationClock, strokeRateToStrokeDuration } from './useAnimationClock';
import { useEffect, useRef } from 'react';

export interface CrewProps {
  /** Current playback state from parent */
  playbackState: PlaybackState;
  /** Callback to update time in parent state */
  onTimeUpdate: (time: number) => void;
  /** Annotation display options */
  annotationOptions?: RowerAnnotationOptions;
}

/**
 * Crew component - positions 8 rowers and coxswain along the hull
 * Rowers are numbered bow to stern (1-8), with stroke (seat 8) closest to stern
 * Coxswain sits in stern behind stroke
 *
 * Manages animation clock based on playback controls
 */
export const Crew = ({ playbackState, onTimeUpdate, annotationOptions }: CrewProps) => {
  const { NUM_ROWERS } = CREW_CONFIG;
  const { crewStartX, seatSpacing, coxswainX } = calculateCrewPositions();

  // Track whether we're currently playing to distinguish scrubbing from playback
  const wasPlayingRef = useRef(playbackState.isPlaying);
  const lastUpdateTimeRef = useRef(Date.now());

  // Custom animation clock that respects playback controls
  const [animationTime, clockControls] = useAnimationClock(
    playbackState.playbackSpeed,
    playbackState.currentTime,
    playbackState.isPlaying
  );

  // Sync play/pause state
  useEffect(() => {
    if (playbackState.isPlaying) {
      clockControls.play();
      wasPlayingRef.current = true;
    } else {
      clockControls.pause();
      wasPlayingRef.current = false;
    }
  }, [playbackState.isPlaying, clockControls]);

  // Sync playback speed
  useEffect(() => {
    clockControls.setSpeed(playbackState.playbackSpeed);
  }, [playbackState.playbackSpeed, clockControls]);

  // Handle external time changes (from scrubber) - respond immediately when paused
  useEffect(() => {
    const externalTime = playbackState.currentTime;
    const timeDiff = Math.abs(animationTime - externalTime);
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

    // If paused and time differs, update immediately (scrubbing)
    // If playing, only update on large jumps (to avoid fighting with animation)
    const threshold = playbackState.isPlaying ? 0.5 : 0.01;

    if (timeDiff > threshold && timeSinceLastUpdate > 50) {
      clockControls.setTime(externalTime);
      lastUpdateTimeRef.current = now;
    }
  }, [playbackState.currentTime, animationTime, playbackState.isPlaying, clockControls]);

  // Update parent time from animation clock
  useEffect(() => {
    // Always update parent time, but only when animation time actually changes
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

    // When playing, update frequently; when paused, only update if time really changed
    if (playbackState.isPlaying || timeSinceLastUpdate > 16) {
      onTimeUpdate(animationTime);
      if (!playbackState.isPlaying) {
        lastUpdateTimeRef.current = now;
      }
    }
  }, [animationTime, playbackState.isPlaying, onTimeUpdate]);

  // Convert stroke rate to stroke duration
  const strokeDuration = strokeRateToStrokeDuration(playbackState.strokeRate);

  return (
    <group>
      {/* 8 Rowers - seats numbered 1 (bow) to 8 (stroke) */}
      {Array.from({ length: NUM_ROWERS }).map((_, i) => {
        const seatNumber = i + 1;
        const x = crewStartX + i * seatSpacing;
        const isPort = isPortSide(seatNumber);

        return (
          <Rower
            key={`rower-${seatNumber}`}
            x={x}
            seatNumber={seatNumber}
            isPort={isPort}
            animationTime={animationTime}
            strokeDuration={strokeDuration}
            annotationOptions={annotationOptions}
          />
        );
      })}

      {/* Coxswain */}
      <Coxswain x={coxswainX} />
    </group>
  );
};
